import React, { useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { MenuItem, Select, ThemeProvider } from "@mui/material";

import "./Dropdown.scss";
import { MUIComponentsTheme } from "../../services/theme-service/ThemeService";
import { Close } from "../svg/svgImageList";
import Button from "../button/Button";
import { translationService } from "../../services/translation/TranslationService";

export interface DropdownOption {
  id: string | number;
  name: string;
  isDefault?: boolean;
}

export interface DropdownProps {
  selectedOption: DropdownOption | null;
  options: DropdownOption[];
  placeholderKey?: string;
  disabled?: boolean;
  onChange?: (newValue: any) => void;
  setFieldValue?: Function;
  fieldName?: string;
  canClearSelection?: boolean;
  onDarkBackground?: boolean;
  fullWidth?: boolean;
}

export const Dropdown = observer((props: DropdownProps) => {
  const {
    selectedOption,
    options,
    onChange,
    disabled,
    setFieldValue,
    fieldName,
    placeholderKey,
    canClearSelection,
    onDarkBackground = false,
  } = props;

  const [value, setValue] = useState<number | string | null>(selectedOption?.id ?? null);

  const handleChange = useCallback(
    (option: number | string | null) => {
      const selectedOption = (option ? options.find((x) => x.id === option) : null) ?? {
        id: 0,
        name: "",
      };
      setValue(selectedOption?.id ?? null);
      if (onChange) onChange(selectedOption);
      if (setFieldValue && fieldName) setFieldValue(fieldName, selectedOption);
    },
    [fieldName, onChange, options, setFieldValue]
  );

  const onClear = useCallback(
    (event: any) => {
      event.stopPropagation();

      setValue(null);

      if (onChange) onChange(null);
      if (setFieldValue && fieldName) setFieldValue(fieldName, null);
    },
    [fieldName, onChange, setFieldValue]
  );

  useEffect(() => {
    if (options.find((option) => option.id.toString() === value?.toString()) === undefined) {
      const defaultOption = options.find((option) => option.isDefault);
      if (defaultOption) {
        setValue(defaultOption.id);
      } else {
        setValue(null);
      }
    }
  }, [fieldName, options, value]);

  useEffect(() => {
    if (!selectedOption) {
      setValue(null);
    } else {
      setValue(selectedOption.id);
    }
  }, [selectedOption]);

  const translatedPlaceholder = placeholderKey ? translationService.translate(placeholderKey) : "";

  return (
    <ThemeProvider theme={MUIComponentsTheme}>
      <Select
        MenuProps={{
          disableScrollLock: true,
        }}
        fullWidth={props.fullWidth ?? false}
        labelId={fieldName}
        className={`dropdown ${onDarkBackground ? "dark-background-dropdown" : ""}`}
        defaultValue={selectedOption?.id}
        value={value ?? ""}
        onChange={(event) => {
          handleChange(event.target.value);
        }}
        displayEmpty
        renderValue={(renderValue: string | number) =>
          renderValue !== undefined && renderValue !== ""
            ? options.find((o) => o.id.toString() === value?.toString())?.name
            : placeholderKey
            ? translatedPlaceholder
            : undefined
        }
        disabled={disabled}
        endAdornment={
          canClearSelection
            ? !!value &&
              !disabled && (
                <Button
                  id="dropdown-clear-selection"
                  onClick={(event) => onClear(event)}
                  icon={{
                    svg: Close,
                    width: 11,
                    height: 11,
                  }}
                />
              )
            : null
        }
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </ThemeProvider>
  );
});
